import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import { IPortalApplicant } from "../../model/applicant/PortalApplicant";
import GetPortalFormsInteractor from "../../interactor/portal/GetPortalFormsInteractor";
import { IReportingPeriod } from "../../model/reporting/ReportingPeriod";
import PortalFormPresentationModel, {
  IPortalFormPresentationModel
} from "../../model/form/PortalFormPresentationModel";
import GetSelectedLanguageInteractor from "../../interactor/settings/GetSelectedLanguageInteractor";
import GetPortalReportingPeriodApplicantDocumentsInteractor from "../../interactor/document/GetPortalReportingPeriodApplicantDocumentsInteractor";
import { IReportingPeriodApplicantDocument } from "../../model/document/ReportingPeriodApplicantDocument";

export interface TPortalReportingPeriodPresenter
  extends TLoadingAwarePresenter {
  applicants: IPortalApplicant[];
  forms: IPortalFormPresentationModel[];
  reportingPeriod: IReportingPeriod;
  reportingPeriodId: number;
  expandSpinnerEnabled?: boolean;
  documents?: IReportingPeriodApplicantDocument[];
}

export interface IPortalReportingPeriodPresenter
  extends TPortalReportingPeriodPresenter,
    TPresentable {
  loadApplicants(applicants: IPortalApplicant[]): void;
  onRowExpanded(expanded: any, record: IPortalApplicant): Promise<void>;
  loadReportingPeriod(reportingPeriod: IReportingPeriod): void;
  showFormDetails(id: number): any;
  onApplicantSelect(applicant: IPortalApplicant): Promise<void>;
}

const defaultState: TPortalReportingPeriodPresenter = {
  applicants: [],
  forms: [],
  reportingPeriod: {} as IReportingPeriod,
  reportingPeriodId: 0,
  documents: undefined
};

const PortalReportingPeriodPresenter = withStore<
  IPortalReportingPeriodPresenter,
  TPortalReportingPeriodPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IPortalReportingPeriodPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TPortalReportingPeriodPresenter>();

    loader.start("pageLoader");

    const loadApplicants = (applicants: IPortalApplicant[]) => {
      _store.update({
        applicants
      });
      loader.stop("pageLoader");
    };

    const loadReportingPeriod = (reportingPeriod: IReportingPeriod) => {
      _store.update({
        reportingPeriod
      });
    };

    const getForms = async (reportingPeriodApplicantId: number) => {
      try {
        let currentLanguage = await _application.container
          .resolve<GetSelectedLanguageInteractor>("getSelectedLanguage")
          .execute();
        if (!currentLanguage) {
          currentLanguage = _application.container.resolve<number>("language");
        }

        let forms = await _application.container
          .resolve<GetPortalFormsInteractor>("getPortalForms")
          .execute(reportingPeriodApplicantId, currentLanguage);
        _store.update({ forms: forms.map(PortalFormPresentationModel) });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const getDocuments = async (reportingPeriodApplicantId: number) => {
      try {
        let documents = await _application.container
          .resolve<GetPortalReportingPeriodApplicantDocumentsInteractor>(
            "getPortalReportingPeriodApplicantDocuments"
          )
          .execute(reportingPeriodApplicantId);
        _store.update({ documents });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const resetApplicantData = () => {
      _store.update({ documents: undefined, forms: [] });
    };

    const onRowExpanded = async (expanded: any, record: IPortalApplicant) => {
      resetApplicantData();
      if (expanded) {
        _store.update({ expandSpinnerEnabled: true });
        await getForms(record.reportingPeriodApplicantId);
        await getDocuments(record.reportingPeriodApplicantId);
        _store.update({ expandSpinnerEnabled: false });
      }
    };

    const showFormDetails = (id: number) => {
      _application.navigator.push({
        pathname: `/forms/${id}`
      });
    };

    const onApplicantSelect = async (applicant: IPortalApplicant) => {
      resetApplicantData();
      await getForms(applicant.reportingPeriodApplicantId);
      await getDocuments(applicant.reportingPeriodApplicantId);
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadApplicants,
      onRowExpanded,
      loadReportingPeriod,
      showFormDetails,
      onApplicantSelect
    };
  },
  defaultState
);
export default PortalReportingPeriodPresenter;
